import { useEffect, useState } from "react";

import { ApiError, apiClient, type Finding, type ReportType, type RunStatus } from "../api/client";
import { ImportStep } from "./weekly-report/ImportStep";
import { RunStep } from "./weekly-report/RunStep";
import { SummaryStep } from "./weekly-report/SummaryStep";
import { ValidationStep } from "./weekly-report/ValidationStep";
import {
  defaultProfileSelection,
  resolveImportSelections,
  selectImportProfile,
} from "./weekly-report/profileSelection";
import { reportTypes, type FlowState } from "./weekly-report/types";

const initialState: FlowState = {
  step: "import",
  profiles: [],
  sources: [],
  selectedSourceId: "",
  selectedProfileIds: {},
  periodStart: "2026-06-15",
  periodEnd: "2026-08-09",
  comparisonWindowWeeks: 4,
  reportingCurrency: "USD",
  currencyRates: '{"USD":"1"}',
  sourceVariants: {},
  files: {},
  reportSet: null,
  issues: [],
  run: null,
  summary: null,
  findings: [],
  searchTerms: [],
  advertisingAdvisory: null,
  selected: null,
  reviews: {},
  busy: false,
  error: null,
};

export function WeeklyReportPage() {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    let current = true;
    Promise.all([apiClient.listImportProfiles(), apiClient.listDataSources()])
      .then(([profiles, sources]) => {
        if (!current) return;
        const selection = defaultProfileSelection(profiles);
        setState((value) => ({
          ...value,
          ...selection,
          profiles,
          sources,
          selectedSourceId: value.selectedSourceId || sources[0]?.id || "",
        }));
      })
      .catch((error: unknown) => current && fail(error, setState));
    return () => {
      current = false;
    };
  }, []);

  async function uploadAndValidate() {
    const missing = reportTypes.filter((item) => !state.files[item]);
    if (missing.length) {
      setState((value) => ({
        ...value,
        error: `缺少 ${missing.length} 份必要报表，无法开始校验。`,
      }));
      return;
    }
    if (!state.periodStart || !state.periodEnd || state.periodEnd < state.periodStart) {
      setState((value) => ({ ...value, error: "请选择有效且有序的报告日期范围。" }));
      return;
    }
    if (!/^[A-Z]{3}$/.test(state.reportingCurrency)) {
      setState((value) => ({ ...value, error: "报告币种必须是三位大写 ISO 代码。" }));
      return;
    }
    let currencyRates: Record<string, string>;
    try {
      currencyRates = parseCurrencyRates(state.currencyRates, state.reportingCurrency);
    } catch {
      setState((value) => ({
        ...value,
        error: "请输入有效的源币种汇率 JSON，且报告币种汇率必须为 1。",
      }));
      return;
    }
    const source = state.sources.find((item) => item.id === state.selectedSourceId);
    if (!source) {
      setState((value) => ({ ...value, error: "请选择当前账号有权使用的店铺数据源。" }));
      return;
    }
    const selections = resolveImportSelections(state);
    if (!selections) {
      setState((value) => ({ ...value, error: "请选择彼此匹配的已发布配置和来源变体。" }));
      return;
    }
    setState((value) => ({ ...value, busy: true, error: null }));
    try {
      const uploads = await Promise.all(
        selections.map((item) => apiClient.uploadRaw(source.id, state.files[item.reportType]!)),
      );
      const imported = await apiClient.importReportSet({
        scope_id: source.scope_id,
        store_code: String(source.source_context.store_code ?? source.code),
        store_display_name: source.display_name,
        site: String(source.source_context.site ?? "US"),
        period_start: state.periodStart,
        period_end: state.periodEnd,
        analysis_config: {
          comparison_window_weeks: state.comparisonWindowWeeks,
          reporting_currency: state.reportingCurrency,
          currency_rates: currencyRates,
          velocity_window_days: 28,
        },
        files: selections.map((selection, index) => {
          return {
            report_type: selection.reportType,
            raw_object_id: uploads[index]!.id,
            profile_version_id: selection.profileId,
            source_variant: selection.sourceVariant,
          };
        }),
      });
      setState((value) => ({
        ...value,
        busy: false,
        step: "validation",
        issues: imported.issues,
        reportSet: imported.report_set,
      }));
    } catch (error) {
      fail(error, setState);
    }
  }

  async function startRun() {
    if (!state.reportSet) return;
    setState((value) => ({ ...value, busy: true, error: null }));
    try {
      const run = await apiClient.startWeeklyReport(state.reportSet.id);
      setState((value) => ({ ...value, busy: false, step: "running", run }));
      await pollRun(run);
    } catch (error) {
      fail(error, setState);
    }
  }

  async function pollRun(initial: RunStatus) {
    let run = initial;
    for (let attempt = 0; attempt < 120; attempt += 1) {
      if (run.state === "completed" && run.snapshot_id) {
        await loadReport(run);
        return;
      }
      if (run.state === "failed" || run.state === "cancelled") {
        setState((value) => ({
          ...value,
          step: "validation",
          run,
          error: "周报运行失败，请检查运行记录后重试。",
        }));
        return;
      }
      await delay(1000);
      run = await apiClient.getRun(run.id);
      setState((value) => ({ ...value, run }));
    }
    setState((value) => ({ ...value, error: "运行仍在继续，可稍后从运行记录恢复查看。" }));
  }

  async function loadReport(run: RunStatus) {
    if (!run.snapshot_id) return;
    const [summary, findings, searchTerms, advertisingAdvisory] = await Promise.all([
      apiClient.getSummary(run.snapshot_id),
      apiClient.listFindings(run.snapshot_id),
      apiClient.listSearchTerms(run.snapshot_id),
      apiClient.listAdvertisingCandidates(run.snapshot_id),
    ]);
    setState((value) => ({
      ...value,
      step: "summary",
      run,
      summary,
      findings,
      searchTerms,
      advertisingAdvisory,
      selected: findings[0] ?? null,
      error: null,
    }));
  }

  async function review(decision: "accepted" | "rejected", note: string) {
    const snapshotId = state.run?.snapshot_id;
    const selected = state.selected;
    if (!snapshotId || !selected) return;
    setState((value) => ({ ...value, busy: true, error: null }));
    try {
      const saved = await apiClient.reviewFinding(snapshotId, selected.finding_key, decision, note);
      setState((value) => ({
        ...value,
        busy: false,
        reviews: { ...value.reviews, [selected.finding_key]: saved },
      }));
    } catch (error) {
      fail(error, setState);
    }
  }

  return (
    <div className="page-stack weekly-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">固定 Workflow · 无模型调用</span>
          <h1>Amazon 每周运营报告与异常预警</h1>
          <p>从三份业务报表生成可重放、可追溯、可人工评审的只读周报。</p>
        </div>
        <ol className="stepper" aria-label="周报生成步骤">
          {(["import", "validation", "running", "summary"] as const).map((step, index) => (
            <li className={step === state.step ? "active" : ""} key={step}>
              <span>{index + 1}</span>
              {["导入", "校验", "运行", "周报"][index]}
            </li>
          ))}
        </ol>
      </header>
      {state.step === "import" && (
        <ImportStep
          onComparisonWindow={(comparisonWindowWeeks) =>
            setState((value) => ({ ...value, comparisonWindowWeeks }))
          }
          onCurrencyRates={(currencyRates) =>
            setState((value) => ({ ...value, currencyRates, error: null }))
          }
          onFile={(reportType: ReportType, file) =>
            setState((value) => ({
              ...value,
              files: { ...value.files, [reportType]: file },
              error: null,
            }))
          }
          onPeriod={(field, period) =>
            setState((value) => ({ ...value, [field]: period, error: null }))
          }
          onProfile={(reportType, profileId) =>
            setState((value) => selectImportProfile(value, reportType, profileId))
          }
          onReportingCurrency={(reportingCurrency) =>
            setState((value) => ({ ...value, reportingCurrency, error: null }))
          }
          onSource={(selectedSourceId) =>
            setState((value) => ({ ...value, selectedSourceId, error: null }))
          }
          onSubmit={() => void uploadAndValidate()}
          onVariant={(reportType, variant) =>
            setState((value) => ({
              ...value,
              sourceVariants: { ...value.sourceVariants, [reportType]: variant },
              error: null,
            }))
          }
          state={state}
        />
      )}
      {state.step === "validation" && (
        <ValidationStep
          onBack={() => setState((value) => ({ ...value, step: "import", error: null }))}
          onStart={() => void startRun()}
          state={state}
        />
      )}
      {state.step === "running" && <RunStep run={state.run} />}
      {state.step === "summary" && (
        <SummaryStep
          onReview={review}
          onSelect={(finding: Finding) =>
            setState((value) => ({ ...value, selected: finding, error: null }))
          }
          state={state}
        />
      )}
    </div>
  );
}

function fail(error: unknown, update: React.Dispatch<React.SetStateAction<FlowState>>) {
  const message = error instanceof ApiError ? error.message : "操作暂时不可用，请稍后重试。";
  update((value) => ({ ...value, busy: false, error: message }));
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function parseCurrencyRates(raw: string, reportingCurrency: string): Record<string, string> {
  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error();
  const result: Record<string, string> = {};
  for (const [currency, value] of Object.entries(parsed)) {
    const normalized = currency.toUpperCase();
    const numeric = Number(value);
    if (!/^[A-Z]{3}$/.test(normalized) || !Number.isFinite(numeric) || numeric <= 0) {
      throw new Error();
    }
    result[normalized] = String(value);
  }
  if (Number(result[reportingCurrency]) !== 1) throw new Error();
  return result;
}
